import {
	type Residence,
	residenceAddPhotos,
	residenceCreatePhotoUpload,
	residenceRemovePhoto,
	residenceReorderPhotos
} from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { DropZone } from '@repo/ui/components/drop-zone'
import { ResidencePlaceholder } from '@repo/ui/components/residence-cover'
import { SortableGrid } from '@repo/ui/components/sortable-grid'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import { type PresignFn, useUpload } from '@repo/ui/hooks/use-upload'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	ArrowLeft,
	ArrowRight,
	ImagePlus,
	MoreHorizontal,
	Star,
	Trash2
} from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'

const MAX_PHOTOS = 10
const MAX_BYTES = 10 * 1024 * 1024
const TYPES = ['image/jpeg', 'image/png', 'image/webp']

/**
 * The photos of the residence, for the general admin and for the facility
 * manager of that residence, laid out like a photo tour: the cover large on
 * the left, the rest around it, dragged into the order the student sees. Each
 * change is saved on its own, in any state of the call: the photos are the
 * showcase, not the terms of the call.
 */
function ResidencePhotos({ residence }: { residence: Residence }) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const inputRef = useRef<HTMLInputElement>(null)
	// The order just dropped, shown until the server confirms it.
	const [order, setOrder] = useState<string[] | null>(null)
	// The residence is read through the console and through the call page.
	const refresh = () =>
		Promise.all([
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.residences.all()
			}),
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.call.all()
			})
		])
	const body = { residenceId: residence.id }

	const add = useMutation({
		mutationFn: (assetIds: string[]) =>
			residenceAddPhotos({ body: { data: { ...body, assetIds } } }),
		onSuccess: refresh,
		onError: (error) => toast.error(formatError(error))
	})
	const reorder = useMutation({
		mutationFn: (photoIds: string[]) =>
			residenceReorderPhotos({ body: { data: { ...body, photoIds } } }),
		onSuccess: refresh,
		onError: (error) => toast.error(formatError(error)),
		onSettled: () => setOrder(null)
	})
	const remove = useMutation({
		mutationFn: (id: string) =>
			residenceRemovePhoto({
				body: { data: { ...body, id } }
			}),
		onSuccess: refresh,
		onError: (error) => toast.error(formatError(error))
	})

	const {
		files,
		upload,
		remove: forget
	} = useUpload({
		presign: presignPhoto(residence.id),
		onComplete: async ({ successful, failed }) => {
			if (failed.length > 0) {
				toast.error(t('admin-residences:photos.upload_failed'))
			}
			if (successful.length > 0) {
				try {
					await add.mutateAsync(
						successful.map((file) => file.backendId)
					)
				} catch {
					// The mutation already told the admin.
				}
			}
			for (const file of [...successful, ...failed]) {
				forget(file.id)
			}
		}
	})

	const photos = order
		? order.flatMap((id) =>
				residence.photos.filter((photo) => photo.id === id)
			)
		: residence.photos
	const uploading = Object.values(files).filter(
		(file) => file.status === 'uploading'
	).length
	const room = MAX_PHOTOS - photos.length - uploading

	function pick(picked: File[]) {
		let accepted = 0
		for (const file of picked) {
			if (!TYPES.includes(file.type)) {
				toast.error(t('admin-residences:photos.invalid_type'))
			} else if (file.size > MAX_BYTES) {
				toast.error(t('admin-residences:photos.too_large'))
			} else if (accepted >= room) {
				toast.error(t('admin-residences:photos.limit'))
				break
			} else if (upload(file)) {
				accepted += 1
			}
		}
	}

	function move(id: string, to: number) {
		const ids = photos.map((photo) => photo.id)
		ids.splice(ids.indexOf(id), 1)
		ids.splice(to, 0, id)
		changeOrder(ids)
	}

	function changeOrder(ids: string[]) {
		setOrder(ids)
		reorder.mutate(ids)
	}

	return (
		<div className='flex flex-col gap-4'>
			<p className='text-muted-foreground text-sm'>
				{photos.length === 0
					? t('admin-residences:photos.empty_hint')
					: t('admin-residences:photos.hint', {
							count: photos.length,
							max: MAX_PHOTOS
						})}
			</p>
			<input
				accept={TYPES.join(',')}
				className='sr-only'
				multiple
				onChange={(event) => {
					pick([...(event.target.files ?? [])])
					event.target.value = ''
				}}
				ref={inputRef}
				tabIndex={-1}
				type='file'
			/>
			{photos.length === 0 && uploading === 0 ? (
				<div className='grid gap-4 md:grid-cols-2'>
					<div className='relative aspect-[4/3] overflow-hidden rounded-xl ring-1 ring-foreground/10'>
						<ResidencePlaceholder
							label={residence.name}
							seed={residence.id}
						/>
						<p className='absolute inset-x-0 bottom-0 bg-background/85 px-3 py-2 text-xs'>
							{t('admin-residences:photos.placeholder_note')}
						</p>
					</div>
					<DropZone
						dropzoneOptions={{
							accept: Object.fromEntries(
								TYPES.map((type) => [type, []])
							),
							maxSize: MAX_BYTES,
							multiple: true,
							onDrop: pick
						}}
					/>
				</div>
			) : (
				<SortableGrid
					className='grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6'
					itemClassName={(index) =>
						index === 0 ? 'col-span-2 md:row-span-2' : undefined
					}
					items={photos}
					onReorder={changeOrder}
					renderItem={(photo, index) => (
						<PhotoTile
							alt={t('admin-residences:photos.alt', {
								n: index + 1,
								name: residence.name
							})}
							cover={index === 0}
							isFirst={index === 0}
							isLast={index === photos.length - 1}
							onMove={(to) => move(photo.id, to)}
							onRemove={() => remove.mutate(photo.id)}
							position={index}
							url={photo.image?.url ?? null}
						/>
					)}
					trailing={
						<>
							{Object.entries(files).map(([id, file]) => (
								<div
									className='relative aspect-[4/3] overflow-hidden rounded-xl bg-muted ring-1 ring-foreground/10'
									key={id}
								>
									<img
										alt=''
										className='size-full object-cover opacity-60'
										height={300}
										src={file.url}
										width={400}
									/>
									<div className='absolute inset-0 flex items-center justify-center'>
										<Spinner />
									</div>
								</div>
							))}
							{room > 0 && (
								<button
									className='flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-xl border border-dashed text-muted-foreground text-sm transition-colors hover:bg-muted/50 hover:text-foreground'
									onClick={() => inputRef.current?.click()}
									type='button'
								>
									<ImagePlus className='size-5' />
									{t('admin-residences:photos.add')}
								</button>
							)}
						</>
					}
				/>
			)}
		</div>
	)
}

function PhotoTile({
	url,
	alt,
	cover,
	position,
	isFirst,
	isLast,
	onMove,
	onRemove
}: {
	url: string | null
	alt: string
	cover: boolean
	position: number
	isFirst: boolean
	isLast: boolean
	onMove: (to: number) => void
	onRemove: () => void
}) {
	const { t } = useTranslation()
	return (
		<div className='group relative aspect-[4/3] h-full w-full overflow-hidden rounded-xl bg-muted ring-1 ring-foreground/10'>
			{url && (
				<img
					alt={alt}
					className='pointer-events-none size-full object-cover'
					draggable={false}
					height={300}
					src={url}
					width={400}
				/>
			)}
			{cover && (
				<span className='absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-background/95 px-2 py-0.5 font-medium text-xs shadow-sm'>
					<Star className='size-3 text-chart-1' />
					{t('admin-residences:photos.cover')}
				</span>
			)}
			<DropdownMenu>
				<DropdownMenuTrigger
					render={
						<Button
							aria-label={t('admin-residences:photos.menu', {
								n: position + 1
							})}
							className='absolute top-2 right-2 rounded-full shadow-sm'
							size='icon-sm'
							type='button'
							variant='secondary'
						/>
					}
				>
					<MoreHorizontal />
				</DropdownMenuTrigger>
				<DropdownMenuContent align='end'>
					{!isFirst && (
						<DropdownMenuItem onClick={() => onMove(0)}>
							<Star />
							{t('admin-residences:photos.make_cover')}
						</DropdownMenuItem>
					)}
					{!isFirst && (
						<DropdownMenuItem onClick={() => onMove(position - 1)}>
							<ArrowLeft />
							{t('admin-residences:photos.move_earlier')}
						</DropdownMenuItem>
					)}
					{!isLast && (
						<DropdownMenuItem onClick={() => onMove(position + 1)}>
							<ArrowRight />
							{t('admin-residences:photos.move_later')}
						</DropdownMenuItem>
					)}
					<DropdownMenuItem onClick={onRemove} variant='destructive'>
						<Trash2 />
						{t('admin-residences:photos.remove')}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)
}

const presignPhoto =
	(residenceId: string): PresignFn =>
	async (file) => {
		const response = await residenceCreatePhotoUpload({
			body: {
				data: {
					residenceId,
					fileName: file.name,
					mimeType: file.type,
					sizeInBytes: file.size
				}
			}
		})
		return { upload: response.data.upload, backendId: response.data.id }
	}

export { ResidencePhotos }
